import useSWR from "swr";

import { useCallback, useEffect, useState } from "react";

import Bugsnag from "@bugsnag/js";
import camelCase from "lodash/camelCase";
import cloneDeep from "lodash/cloneDeep";

import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher, fetchApi } from "utils/baseFetcher";

import { getMutedItemsUrl } from "../utils";

const useMuteSettings = () => {
  const { account } = useAuthentication();
  const { setNotification } = useNotification();

  const [settings, setSettings] = useState(account?.mutedContents);

  useEffect(() => {
    setSettings(account?.mutedContents);
  }, [account?.mutedContents]);

  const { data: mutedAccountsResponse, mutate: mutateMutedAccounts } = useSWR(
    getMutedItemsUrl("account"),
    baseFetcher
  );
  const mutedAccounts = mutedAccountsResponse?.data || [];

  const { data: mutedCompaniesResponse, mutate: mutateMutedCompanies } = useSWR(
    getMutedItemsUrl("company"),
    baseFetcher
  );
  const mutedCompanies = mutedCompaniesResponse?.data || [];

  const { data: mutedEntitiesResponse, mutate: mutateMutedEntities } = useSWR(
    getMutedItemsUrl("entity"),
    baseFetcher
  );
  const mutedEntities = mutedEntitiesResponse?.data || [];

  const deleteMutedItem = useCallback(
    async (item: any) => {
      try {
        await fetchApi(`/api/v2/muted-items/${item?.id}`, {
          method: "DELETE",
        });

        item.isDeleted = true;

        const callback = (oldData: any) => ({
          ...oldData,
          data:
            oldData?.data?.filter?.((i: any) => {
              return i.id !== item.id;
            }) || [],
        });

        if (item.mutedCompany) {
          mutateMutedCompanies(callback(mutedCompaniesResponse));
        } else if (item.mutedAccount) {
          mutateMutedAccounts(callback(mutedAccountsResponse));
        } else if (item.mutedEntity) {
          mutateMutedEntities(callback(mutedEntitiesResponse));
        }

        setNotification("Mute settings updated!", "success");
      } catch (error: any) {
        Bugsnag.notify(error);
        setNotification("Something went wrong!", "error");
      }
    },
    [
      mutateMutedAccounts,
      mutateMutedCompanies,
      mutateMutedEntities,
      mutedAccountsResponse,
      mutedCompaniesResponse,
      mutedEntitiesResponse,
      setNotification,
    ]
  );

  const updateMuteContents = useCallback(
    async (content: string) => {
      try {
        const newSettings = cloneDeep(settings);
        newSettings[camelCase(content)] = !newSettings[camelCase(content)];

        setSettings(newSettings);

        await fetchApi(`/api/v2/accounts/${account?.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            data: {
              attributes: {
                "muted-contents": {
                  [content]: newSettings[camelCase(content)],
                },
              },
              id: account?.id,
              relationships: {},
              type: "accounts",
            },
          }),
        });

        setNotification("Mute settings updated!", "success");
      } catch (error: any) {
        Bugsnag.notify(error);
        setNotification("Something went wrong!", "error");
      }
    },
    [account?.id, setNotification, settings]
  );

  const onMuteClick = useCallback(
    async (name: string, type: string, mutedData: any) => {
      try {
        const reqestBody: any = {
          data: {
            relationships: {
              account: {
                data: {
                  type: "accounts",
                  id: account?.id,
                },
              },
            },
            type: "muted-items",
            attributes: {},
          },
        };

        const mutedItem = {
          data: {
            type,
            id: "",
          },
        };

        mutedItem.data.id = mutedData?.id;

        reqestBody.data.relationships[`muted-${name}`] = mutedItem;

        const expireAt = new Date();
        expireAt.setFullYear(expireAt.getFullYear() + 10);
        reqestBody.data.attributes["expire-at"] = expireAt;

        const data = await fetchApi("/api/v2/muted-items", {
          method: "POST",
          body: JSON.stringify(reqestBody),
        });

        setNotification("Mute settings updated!", "success");

        switch (name) {
          case "account":
            mutateMutedAccounts((items: any) => {
              const newState = cloneDeep(items);
              return newState.map((i: any) => {
                if (mutedData.id === i.mutedAccount.id) {
                  i.id = data.id;
                  i.isDeleted = false;
                }
                return i;
              });
            });
            break;
          case "entity":
            mutateMutedEntities((items: any) => {
              const newState = cloneDeep(items);
              return newState.map((i: any) => {
                if (mutedData.id === i.mutedEntity.id) {
                  i.id = data.id;
                  i.isDeleted = false;
                }
                return i;
              });
            });
            break;
          case "company":
            mutateMutedCompanies((items: any) => {
              const newState = cloneDeep(items);
              return newState.map((i: any) => {
                if (mutedData.id === i.mutedCompany.id) {
                  i.id = data.id;
                  i.isDeleted = false;
                }
                return i;
              });
            });
            break;
        }
      } catch (error: any) {
        Bugsnag.notify(error);
        setNotification("Something went wrong!", "error");
      }
    },
    [
      account?.id,
      mutateMutedAccounts,
      mutateMutedCompanies,
      mutateMutedEntities,
      setNotification,
    ]
  );

  return {
    mutedAccounts,
    mutedEntities,
    deleteMutedItem,
    updateMuteContents,
    settings,
    mutedCompanies,
    onMuteClick,
  };
};

export default useMuteSettings;
